import { Typography } from "@mui/material";

import { ConfirmDialog } from "../ConfirmDialog";
import { CrossTenantMismatch } from "./lib/crossTenant.helpers";

type CrossTenantPaymentConfirmDialogProps = {
  mismatch: CrossTenantMismatch;
  /** True while the retried save is in flight. */
  loading?: boolean;
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
};

/**
 * Asks staff to confirm a payment whose association belongs to an organization
 * outside the payment's partner family. Names both organizations and which one
 * is billed, since the fee follows the facility's organization, not the
 * payment's.
 */
export function CrossTenantPaymentConfirmDialog({
  mismatch,
  loading,
  onConfirm,
  onClose,
}: CrossTenantPaymentConfirmDialogProps) {
  const target = mismatch.targetKind.toLowerCase();
  return (
    <ConfirmDialog
      open
      loading={loading}
      header="Record a payment for another organization?"
      preResourcesInfo={
        <Typography sx={{ marginBottom: 2 }}>
          This {target} belongs to {mismatch.targetTenantName}, not {mismatch.paymentTenantName}.
        </Typography>
      }
      resources={[
        { resourceType: `${mismatch.targetKind} owner`, resourceName: mismatch.targetTenantName },
        { resourceType: "Payment recorded under", resourceName: mismatch.paymentTenantName },
        { resourceType: "Fees billed to", resourceName: mismatch.targetTenantName },
      ]}
      info={`Cross-organization payments are unusual. Continue only if this payment really covers a filing for ${mismatch.targetTenantName}, such as when the organizations share a state portal account.`}
      confirmText={`Yes, record for ${mismatch.targetTenantName}`}
      cancelText="Go back"
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
